<template>
  <UDrawer
    v-model:open="isOpen"
    :dismissible="false"
    :handle="false"
    :ui="{
      content: 'w-full h-full',
      overlay: 'bg-black/50',
    }"
  >
    <template #content>
      <!-- Header -->
      <div
        class="flex items-center gap-4 p-6 border-b border-gray-200 dark:border-gray-700"
      >
        <UButton
          color="neutral"
          variant="ghost"
          icon="i-lucide-arrow-left"
          @click="closeDrawer"
        >
          Retour
        </UButton>
        <h3 class="text-lg font-semibold">
          {{
            menuContext?.isMenuMode
              ? `Personnaliser ${localMeal.name} (${menuContext.menuName})`
              : `Personnaliser ${localMeal.name}`
          }}
        </h3>
      </div>

      <!-- Main Content -->
      <div class="flex-1 overflow-y-auto">
        <div class="p-6">
          <!-- Meal Info Section -->
          <div class="flex gap-6 mb-8">
            <!-- Left: Picture -->
            <div class="w-48 h-48 flex-shrink-0">
              <NuxtImg
                :src="
                  localMeal.image?.startsWith('http')
                    ? localMeal.image
                    : getImageUrl(localMeal.image)
                "
                :alt="localMeal.name"
                class="w-full h-full object-cover rounded-lg shadow-md"
                loading="lazy"
              />
            </div>

            <!-- Right: Meal Details -->
            <div class="flex-1">
              <h2
                class="text-2xl font-bold text-main-color dark:text-main-color-400 mb-2"
              >
                {{ localMeal.name }}
              </h2>
              <p
                class="text-3xl font-bold text-main-color dark:text-main-color-400 mb-4"
              >
                {{ localMeal.price }}€
              </p>

              <!-- Available Menus -->
              <div
                v-if="
                  localMeal.menu_content && localMeal.menu_content.length > 0
                "
                class="mb-6"
              >
                <h3 class="text-lg font-semibold mb-3">Available Menus:</h3>
                <div class="space-y-2">
                  <div
                    v-for="menu in localMeal.menu_content"
                    :key="menu.id"
                    class="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
                  >
                    <div
                      v-if="menu.repas"
                      class="flex justify-between items-center"
                    >
                      <span class="font-medium">{{ menu.repas.name }}</span>
                      <span
                        class="text-main-color dark:text-main-color-400 font-semibold"
                      >
                        +{{ menu.repas.price }}€
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Extra Ingredients Section -->
          <div>
            <div>
              <h3 class="text-lg font-semibold mb-4">
                Personnaliser les ingrédients
              </h3>
              <div v-if="loadingIngredients" class="flex justify-center py-8">
                <div
                  class="animate-spin rounded-full h-8 w-8 border-b-2 border-main-color"
                />
              </div>
              <div
                v-else
                class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
              >
                <div
                  v-for="ingredient in availableIngredients"
                  :key="ingredient.id"
                  class="flex flex-col items-center gap-3 p-4 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 hover:shadow-md transition-shadow"
                >
                  <NuxtImg
                    :src="ingredient.image"
                    :alt="ingredient.name"
                    class="w-24 h-24 object-cover rounded-lg"
                    loading="lazy"
                  />
                  <div class="text-center">
                    <div class="h-10 flex items-center justify-center">
                      <p
                        v-if="
                          (selectedExtras[ingredient.id] ?? 0) === 0 &&
                          getOriginalQuantity(ingredient.id) > 0
                        "
                        class="font-bold text-sm text-red-500"
                      >
                        Sans {{ ingredient.name }}
                      </p>
                      <p v-else class="font-medium text-sm">
                        {{ ingredient.name }}
                      </p>
                    </div>
                    <p
                      class="text-sm text-main-color dark:text-main-color-400 font-semibold"
                    >
                      +{{ ingredient.price }}€
                    </p>
                  </div>
                  <div class="flex items-center gap-2">
                    <UButton
                      size="xs"
                      variant="outline"
                      icon="i-lucide-minus"
                      :disabled="!selectedExtras[ingredient.id]"
                      @click="decreaseExtra(ingredient.id)"
                    />
                    <span class="w-8 text-center text-sm">
                      {{ selectedExtras[ingredient.id] || 0 }}
                    </span>
                    <UButton
                      size="xs"
                      variant="outline"
                      icon="i-lucide-plus"
                      :disabled="
                        (selectedExtras[ingredient.id] || 0) >=
                        getMaxAllowed(ingredient.id)
                      "
                      @click="increaseExtra(ingredient.id)"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="p-6 border-t border-gray-200 dark:border-gray-700">
          <div class="flex justify-between items-center">
            <div>
              <p class="text-sm text-gray-500 dark:text-gray-400">
                Coût des suppléments :
              </p>
              <p
                class="text-xl font-bold text-main-color dark:text-main-color-400"
              >
                +{{ totalExtraCost.toFixed(2) }}€
              </p>
            </div>
            <UButton color="main" size="lg" @click="addCustomizedToCart">
              {{
                menuContext?.isMenuMode
                  ? "Confirmer la personnalisation"
                  : "Ajouter au panier"
              }}
              ({{ (localMeal.price + totalExtraCost).toFixed(2) }}€)
            </UButton>
          </div>
        </div>
      </div>
    </template>
  </UDrawer>
</template>

<script setup lang="ts">
import type { Repas, Ingredient } from "~/models/repas";

interface Props {
  meal: Repas;
  modelValue: boolean;
  // Props pour la réutilisation avec les menus
  menuContext?: {
    menuId: number;
    menuName: string;
    isMenuMode: boolean;
  };
  // Option pour désactiver l'ajout automatique au panier
  autoAddToCart?: boolean;
  // Option pour passer les extras existants (modification)
  existingExtras?: Array<{
    ingredient: Ingredient;
    quantity: number;
  }>;
}

const props = withDefaults(defineProps<Props>(), {
  autoAddToCart: true,
  menuContext: undefined,
  existingExtras: undefined,
});

// Local reactive copy of meal to handle hydration if needed
const localMeal = ref<Repas>({ ...props.meal });

// Update localMeal when props.meal changes
watch(
  () => props.meal,
  (newMeal) => {
    localMeal.value = { ...newMeal };
  },
  { deep: true },
);

interface Emits {
  (e: "update:modelValue", value: boolean): void;
  // Émissions pour la réutilisation avec les menus
  (
    e: "meal-customized",
    data: {
      meal: Repas;
      extras: Array<{
        ingredient: Ingredient;
        quantity: number;
      }>;
      extraCost: number;
      menuContext?: {
        menuId: number;
        menuName: string;
      };
    },
  ): void;
}

const emit = defineEmits<Emits>();

const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

const selectedExtras = ref<Record<number, number>>({});
const availableIngredients = ref<Ingredient[]>([]);
const loadingIngredients = ref(false);

const { $api } = useNuxtApp();
const cartStore = useCartStore();

// Fetch available ingredients from API
const fetchAvailableIngredients = async () => {
  loadingIngredients.value = true;
  try {
    const response = await $api.get("/ingredients-categorie", "include");

    if (response && Array.isArray(response)) {
      // Flatten all ingredients from all categories
      const allIngredients: Ingredient[] = [];

      response.forEach((category: { ingredients?: Ingredient[] }) => {
        if (category.ingredients && Array.isArray(category.ingredients)) {
          category.ingredients.forEach((ingredient: Ingredient) => {
            allIngredients.push({
              id: ingredient.id,
              name: ingredient.name,
              price: ingredient.price,
              is_supplement: ingredient.is_supplement,
              is_halal: ingredient.is_halal,
              is_vegan: ingredient.is_vegan,
              image: getImageUrl(ingredient.image),
            });
          });
        }
      });

      availableIngredients.value = allIngredients;
    }
  } catch (error) {
    console.error("Error fetching ingredients:", error);
  } finally {
    loadingIngredients.value = false;
  }
};

// Fetch full meal details if content/ingredients are missing
const fetchFullMealDetails = async () => {
  if (!localMeal.value.id) return;

  try {
    const res = await $api.get(`/repas/${localMeal.value.id}`);
    if (res) {
      // Preserve existing properties but overwrite with full details
      localMeal.value = {
        ...localMeal.value,
        ...res,
        // Ensure image is mapped correctly if it comes fresh from API
        image:
          res.image && !res.image.startsWith("http")
            ? getImageUrl(res.image)
            : res.image || localMeal.value.image,
        content: res.content || [],
      };
    }
  } catch (e) {
    console.error("Failed to fetch full meal details", e);
  }
};

// Parse meal ingredients when available ingredients are loaded
const parseMealIngredients = () => {
  // Method 1: Use structured content (Preferred)
  if (
    localMeal.value.content &&
    Array.isArray(localMeal.value.content) &&
    localMeal.value.content.length > 0
  ) {
    localMeal.value.content.forEach(
      (item: { ingredient?: { id: number }; quantity: number }) => {
        // item structure: { quantity: number, ingredient: { id: number, name: string, ... } }
        if (item.ingredient && item.ingredient.id) {
          selectedExtras.value[item.ingredient.id] = item.quantity;
        }
      },
    );

    return;
  }

  // Method 2: Fallback to string parsing (Legacy)

  if (localMeal.value.ingredients && availableIngredients.value.length > 0) {
    const ingredientStrings = localMeal.value.ingredients
      .split(",")
      .map((s: string) => s.trim());

    ingredientStrings.forEach((ingredientString: string) => {
      // Extract quantity and name from strings like "1 Salade", "3 Steak"
      // Updated to handle implicit quantity "Salade" -> "1 Salade"
      const match = ingredientString.match(/^(\d+)\s+(.+)$/);
      let quantity = 1;
      let ingredientName = ingredientString;

      if (match) {
        quantity = parseInt(match[1]);
        ingredientName = match[2].trim();
      } else {
        ingredientName = ingredientString.trim();
      }

      // Find matching ingredient in available ingredients
      const matchingIngredient = availableIngredients.value.find(
        (ing) => ing.name.toLowerCase() === ingredientName.toLowerCase(),
      );

      if (matchingIngredient) {
        selectedExtras.value[matchingIngredient.id] = quantity;
      }
    });
  }
};

// Apply existing extras to selectedExtras
const applyExistingExtras = () => {
  if (props.existingExtras && props.existingExtras.length > 0) {
    props.existingExtras.forEach((extra) => {
      selectedExtras.value[extra.ingredient.id] = extra.quantity;
    });
  }
};

// Watch for drawer open to fetch ingredients
watch(
  isOpen,
  async (newValue) => {
    if (newValue) {
      const promises: Promise<void>[] = [];

      // Check if meal needs hydration (missing content AND ingredients)
      const needsHydration =
        (!localMeal.value.content || localMeal.value.content.length === 0) &&
        (!localMeal.value.ingredients ||
          localMeal.value.ingredients.trim() === "");

      if (needsHydration) {
        promises.push(fetchFullMealDetails());
      }

      if (availableIngredients.value.length === 0) {
        promises.push(fetchAvailableIngredients());
      }

      // Wait for both to complete parallelly
      if (promises.length > 0) {
        await Promise.all(promises);
      }

      // Parse logic handles both hydration path and normal path

      parseMealIngredients();
      applyExistingExtras();
    } else {
      // Reset selections when drawer closes
      selectedExtras.value = {};
    }
  },
  { immediate: true },
);

// Watch for when ingredients are loaded to parse meal ingredients
watch(availableIngredients, () => {
  if (availableIngredients.value.length > 0) {
    parseMealIngredients();
    applyExistingExtras();
  }
});

// Helper function to get original quantity from meal ingredients
const getOriginalQuantity = (ingredientId: number) => {
  // Method 1: Use structured content
  if (localMeal.value.content && Array.isArray(localMeal.value.content)) {
    const item = localMeal.value.content.find(
      (c: { ingredient?: { id: number } }) =>
        c.ingredient && c.ingredient.id === ingredientId,
    );
    if (item) return item.quantity;
  }

  // Method 2: Fallback to string parsing
  if (!localMeal.value.ingredients) return 0;

  const ingredientStrings = localMeal.value.ingredients
    .split(",")
    .map((s: string) => s.trim());

  for (const ingredientString of ingredientStrings) {
    const match = ingredientString.match(/^(\d+)\s+(.+)$/);
    let quantity = 1;
    let ingredientName = ingredientString;

    if (match) {
      quantity = parseInt(match[1]);
      ingredientName = match[2].trim();
    } else {
      ingredientName = ingredientString.trim();
    }

    const matchingIngredient = availableIngredients.value.find(
      (ing) =>
        ing.id === ingredientId &&
        ing.name.toLowerCase() === ingredientName.toLowerCase(),
    );

    if (matchingIngredient) {
      return quantity;
    }
  }
  return 0;
};

// Computed properties
const totalExtraCost = computed(() => {
  return Object.entries(selectedExtras.value).reduce(
    (total, [ingredientId, quantity]) => {
      const ingredient = availableIngredients.value.find(
        (ing) => ing.id === parseInt(ingredientId),
      );

      if (!ingredient) return total;

      // Get the original quantity included in the meal
      const originalQuantity = getOriginalQuantity(parseInt(ingredientId));

      // Only charge for additional ingredients beyond the original
      const additionalQuantity = Math.max(0, quantity - originalQuantity);

      return total + ingredient.price * additionalQuantity;
    },
    0,
  );
});

// Get max allowed quantity for an ingredient (Original + 2)
const getMaxAllowed = (ingredientId: number) => {
  const originalQuantity = getOriginalQuantity(ingredientId);
  return originalQuantity + 2;
};

// Methods
const closeDrawer = () => {
  isOpen.value = false;
};

const increaseExtra = (ingredientId: number) => {
  const current = selectedExtras.value[ingredientId] || 0;
  const maxAllowed = getMaxAllowed(ingredientId);

  if (current < maxAllowed) {
    selectedExtras.value[ingredientId] = current + 1;
  }
};

const decreaseExtra = (ingredientId: number) => {
  const current = selectedExtras.value[ingredientId] || 0;
  if (current > 0) {
    const newQuantity = current - 1;
    // Just set to newQuantity (even if 0), do not delete key
    // This ensures that for base ingredients, we track the explicit "0" state
    selectedExtras.value[ingredientId] = newQuantity;
  }
};

const addCustomizedToCart = () => {
  const extras = Object.entries(selectedExtras.value)
    .filter(([ingredientId, quantity]) => {
      // Keep if quantity > 0 OR if it's a base ingredient (original > 0) so we track removal
      if (quantity > 0) return true;
      const original = getOriginalQuantity(parseInt(ingredientId));
      return original > 0;
    })
    .map(([ingredientId, quantity]) => ({
      ingredient: availableIngredients.value.find(
        (ing) => ing.id === parseInt(ingredientId),
      )!,
      quantity,
    }))
    .filter((extra) => extra.ingredient);

  emit("meal-customized", {
    meal: localMeal.value,
    extras,
    extraCost: totalExtraCost.value,
    menuContext: props.menuContext,
  });

  // Ajouter au panier par défaut, sauf si explicitement désactivé

  if (props.autoAddToCart !== false) {
    cartStore.addCustomizedMeal(localMeal.value, extras);
  }

  // Close drawer after adding
  closeDrawer();
};
</script>
